import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { useDesignTokensTheme } from "@swa-ui/design-tokens";
import { classNames } from "@swa-ui/string";

import { Area } from "../Area";
import { Button } from "../Button";
import { Icon } from "../Icon";
import styles from "./Accordion.module.scss";

/**
 * The Accordion component delivers large amounts of content in a small space through progress disclosure. The header
 * title gives the traveler a high level overview of the content allowing the traveler to decide which sections to read.

 * Accordions can make information processing and discovering more effective. However, it does hide content from
 * travelers and it's important to account for a traveler not noticing or reading all the included content.

 * When to use
 * To organize related information.
 * To shorten pages and reduce scrolling when all content is not crucial to read in full.
 * When space is at a premium and long content cannot be displayed all at once, like on a mobile interface or in a side
 * panel.
 */

const ACCORDION_ICON_DISABLED_DESIGN_TOKEN = "cmp-core-color-accordion-icon-disabled";
const ACCORDION_LABEL_FG_DESIGN_TOKEN = "cmp-core-color-accordion-label-fg";

export const Accordion = (props) => {
  const {
    children,
    className,
    disabled,
    fullWidthContent = false,
    indicatorClassName,
    label,
    labelBorder = true,
    name,
    noBackground = false,
    onChange,
    revealed = false,
    styleType = "primary",
    type,
  } = props;
  const { themeClass } = useDesignTokensTheme({ component: "accordion", styleType });
  const [reveal, setReveal] = useState(revealed);

  useEffect(() => {
    setReveal(revealed);
  }, [revealed]);

  return (
    <div className={getClass()}>
      <div {...getLabelProps()}>
        <Button {...getButtonProps()}>
          {styleType === "no-style" ? label : renderStandardLabel()}
        </Button>
      </div>
      <Area {...getContentProps()}>{children}</Area>
    </div>
  );

  function renderStandardLabel() {
    return (
      <>
        {label}
        <Icon {...getIndicatorProps()} />
      </>
    );
  }

  function getButtonProps() {
    return {
      alignment: "left",
      "aria-controls": name,
      "aria-expanded": reveal,
      className: styles.button,
      disabled,
      onClick: handleClickReveal,
      styleType: "no-style",
    };
  }

  function getContentProps() {
    return {
      className: classNames({ [styles.contentContainer]: !fullWidthContent }),
      id: name,
      revealed: reveal,
    };
  }

  function getIndicatorProps() {
    const color = disabled ? ACCORDION_ICON_DISABLED_DESIGN_TOKEN : ACCORDION_LABEL_FG_DESIGN_TOKEN;
    const key = reveal ? "collapse" : "expand";

    return {
      "aria-label": props[`aria-label-${key}`],
      className: classNames(styles.indicator, indicatorClassName),
      color,
      custom: { type: reveal ? "inactive" : "active" },
      name: "ArrowThin",
      role: "presentation",
    };
  }

  function getLabelProps() {
    return {
      "aria-level": props["aria-level"] ?? 2,
      className: classNames(styles.label, {
        [styles.labelBorder]: labelBorder,
        [styles.noBackground]: noBackground,
      }),
      role: "heading",
    };
  }

  function getClass() {
    return classNames(className, styles.accordion, themeClass, {
      [styles.disabled]: disabled,
      [styles.noBackground]: noBackground,
    });
  }

  function handleClickReveal() {
    const newRevealState = !reveal;

    setReveal(newRevealState);
    onChange && onChange(newRevealState);
  }
};

Accordion.displayName = "Accordion";

Accordion.propTypes = {
  /** Aria level for the heading. The value should be a number between 1 and 6. Defaults to 2. */
  "aria-level": PropTypes.number,

  /** Content that will be rendered when the accordion is expanded. */
  children: PropTypes.node.isRequired,

  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /**
   * Indicates if a click to open/close should be ignored and if gray styling should be applied. Only text color will
   * be set for the label, which will be sufficient for pure text, but if JSX is given for label, the calling
   * application will need to provide disabled styling.
   */
  disabled: PropTypes.bool,

  /** Indicates if the rendered content should have any default padding or margin applied */
  fullWidthContent: PropTypes.bool,

  /** Additional classes for styling the indicator. */
  indicatorClassName: PropTypes.string,

  /* Content that will be rendered in the clickable area of Accordion that is always visible. */
  label: PropTypes.node.isRequired,

  /** Indicates if the label will display a border color. Different border color
   *  is set based on the styleType.  */
  labelBorder: PropTypes.bool,

  /**
   * <code>name</code> will be used to tie the clickable area with Accordion's content. The given name is used for
   * the aria-controls attribute to be A11Y compliant.
   */
  name: PropTypes.string.isRequired,

  /**
   * Removes the background color from the accordion. Has no effect when styleType is "no-style"
   * since it already has no background color.
   */
  noBackground: PropTypes.bool,

  /**
   * Function to be called when the accordion is expanded or collapsed.
   * @return {bool} true/false if accordion is open/closed
   */
  onChange: PropTypes.func,

  /** Indicates if the accordion is revealed or concealed. */
  revealed: PropTypes.bool,

  /**
   * styleType controls how the accordion is rendered with regards to the color scheme and amount of spacing.
   */
  styleType: PropTypes.oneOf(["no-style", "primary", "secondary", "tertiary"]),

  /**
   * DEPRECATED: type controls how the accordion is rendered with regards to the color scheme and amount of spacing.
   */
  type: PropTypes.oneOf(["primary", "secondary"]),
};
